import { useTranslation } from '@/hooks/useTranslation';
import React from 'react';

interface LoadingStateProps {
  /**
   * Custom loading message. If not provided, uses default translation
   */
  message?: string;
  /**
   * Minimum height for the loading container
   */
  minHeight?: string;
  /**
   * Custom spinner size
   */
  spinnerSize?: 'sm' | 'md' | 'lg';
  /**
   * Show or hide the loading message
   */
  showMessage?: boolean;
}

/**
 * Reusable loading state component with spinner and optional message
 */
export const LoadingState: React.FC<LoadingStateProps> = React.memo(
  ({
    message,
    minHeight = '400px',
    spinnerSize = 'md',
    showMessage = true,
  }) => {
    const { t } = useTranslation();

    const spinnerSizes = {
      sm: 'h-6 w-6',
      md: 'h-8 w-8',
      lg: 'h-12 w-12',
    };

    const displayMessage = message || t('loading');

    return (
      <div className='flex items-center justify-center' style={{ minHeight }}>
        <div className='text-center'>
          <div
            className={`animate-spin rounded-full border-b-2 border-primary mx-auto ${spinnerSizes[spinnerSize]}`}
            aria-label={displayMessage}
          ></div>
          {showMessage && (
            <p className='mt-2 text-muted-foreground'>{displayMessage}</p>
          )}
        </div>
      </div>
    );
  }
);

LoadingState.displayName = 'LoadingState';

export default LoadingState;
